import React, { useState, useRef } from 'react';
import { API_BASE_URL } from '../services/api';

const Login = ({ onLogin = () => {} }) => {
  const [formData, setFormData] = useState({
    mobile: '',
    otp: ''
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showOTP, setShowOTP] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const otpRefs = useRef([]);
const handleInputChange = (e) => {
  const { name, value } = e.target;
  
  // For mobile number, only allow digits and limit to 10 digits
  if (name === 'mobile') {
    const cleanValue = value.replace(/\D/g, '').slice(0, 10);
    setFormData({
      ...formData,
      [name]: cleanValue
    });
  } else if (name === 'otp') {
    // For OTP, only allow digits and limit to 4 digits
    const cleanValue = value.replace(/\D/g, '').slice(0, 4);
    setFormData({
      ...formData,
      [name]: cleanValue
    });
  } else {
    setFormData({
      ...formData,
      [name]: value
    });
  }
  
  // Clear error when user starts typing
  if (error) setError('');
};

  const handleOTPChange = (e, index) => {
    const value = e.target.value.replace(/\D/g, ''); // Only allow digits
    
    if (value.length <= 1) {
      const newOTP = formData.otp.split('');
      newOTP[index] = value;
      
      setFormData({
        ...formData,
        otp: newOTP.join('')
      });

      // Auto-focus next input
      if (value && index < 3) {
        otpRefs.current[index + 1]?.focus();
      }
    }
    
    // Clear error when user starts typing
    if (error) setError('');
  };

  const handleOTPKeyDown = (e, index) => {
    // Handle backspace
    if (e.key === 'Backspace' && !formData.otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
    
    // Handle arrow keys
    if (e.key === 'ArrowLeft' && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
    if (e.key === 'ArrowRight' && index < 3) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  const handleOTPPaste = (e, index) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 4);
    
    if (pastedData) {
      setFormData({
        ...formData,
        otp: pastedData
      });
      
      // Focus the last filled input or the next empty one
      const nextIndex = Math.min(pastedData.length - 1, 3);
      otpRefs.current[nextIndex]?.focus();
    }
  };

  const handleSendOTP = async () => {
    if (!formData.mobile || formData.mobile.length !== 10) {
      setError('Please enter a valid 10-digit mobile number');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE_URL}/login/${formData.mobile}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      setShowOTP(true);
      setOtpSent(true);
      
    } catch (err) {
      setError('Failed to send OTP. Please try again.');
      console.error('Send OTP error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async () => {
    if (!formData.otp || formData.otp.length !== 4) {
      setError('Please enter the 4-digit OTP');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE_URL}/login/${formData.mobile}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          otp: parseInt(formData.otp)
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Store token and user data in localStorage
      if (data.token) {
        localStorage.setItem('token', data.token);
      }
      if (data.user) {
        localStorage.setItem('user', JSON.stringify(data.user));
      }
      
      // Call onLogin callback if provided
      onLogin(data);
      
      // Route to dashboard
      window.location.href = '/dashboard';
      
    } catch (err) {
      setError('Invalid OTP. Please try again.');
      console.error('Login error:', err);
    } finally {
      setLoading(false);
    }
  };

const handleResendOTP = async () => {
  setFormData({ ...formData, otp: '' });
  setError('');
  await handleSendOTP();
};

  return (
    <div className="h-screen flex flex-col lg:flex-row overflow-hidden">
      {/* Left Side - Form */}
      <div className="flex-1 bg-blue-50 flex items-center justify-center min-h-0">
        <div className="w-full h-full flex items-center justify-center p-4 sm:p-6 lg:p-8">
          <div className="w-full max-w-sm sm:max-w-md">
            <div className="text-center mb-6 sm:mb-8">
              <img 
                src="images/tnlogo.png" 
                alt="Logo" 
                className="h-16 sm:h-20 mx-auto mb-4"
              />
              <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-blue-800 mb-2">
                Rural Housing Works Inspection
              </h1>
              <p className="text-xs sm:text-sm text-gray-700 font-medium">
                Rural Development and Panchayat Raj, Villupuram
              </p>
            </div>
            
            <div className="space-y-4 sm:space-y-5">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm">
                  {error}
                </div>
              )}

              {/* Mobile Number Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Mobile Number</label>
                <div className="relative">
                  <input 
                    type="tel"
                    name="mobile"
                    value={formData.mobile}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 sm:px-4 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base"
                    placeholder="Enter 10-digit mobile number"
                    disabled={loading || otpSent}
                    maxLength="10"
                  />
                  {formData.mobile && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-xs text-gray-500">
                      {formData.mobile.length}/10
                    </div>
                  )}
                </div>
              </div>
              
              {/* Send OTP Button */}
              {!showOTP && (
                <button 
                  type="button"
                  onClick={handleSendOTP}
                  disabled={loading || !formData.mobile || formData.mobile.length !== 10}
                  className="w-full bg-blue-600 text-white py-2 sm:py-3 rounded-lg hover:bg-blue-700 transition duration-200 font-medium text-sm sm:text-base disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Sending OTP...' : 'Send OTP'}
                </button>
              )}

              {/* OTP Input */}
              {showOTP && (
                <>
                 <div>
  <label className="block text-sm font-medium text-gray-700 mb-2">
    Enter OTP
  </label>
  <p className="text-xs text-gray-600 mb-3">
    OTP sent to +91 {formData.mobile}
  </p>
  <input 
    type="tel"
    name="otp"
    value={formData.otp}
    onChange={handleInputChange}
    className="w-full px-3 py-2 sm:px-4 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base"
    placeholder="Enter 4-digit OTP"
    disabled={loading}
    maxLength="4"
  />
</div>

                  {/* Login Button */}
                  <button 
                    type="button"
                    onClick={handleLogin}
                    disabled={loading || !formData.otp || formData.otp.length !== 4}
                    className="w-full bg-blue-600 text-white py-2 sm:py-3 rounded-lg hover:bg-blue-700 transition duration-200 font-medium text-sm sm:text-base disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? 'Verifying...' : 'Login'}
                  </button>

                  {/* Resend OTP */}
                  <div className="text-center">
                    <button 
                      type="button"
                      onClick={handleResendOTP}
                      disabled={loading}
                      className="text-blue-600 hover:text-blue-800 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Resend OTP
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Image */}
      <div className="flex-1 hidden lg:block">
        <img 
          src="images/login.png" 
          alt="Housing Inspection" 
          className="w-full h-full object-cover"
        />
      </div>
    </div>
  );
};

export default Login;